import _ from 'lodash'

import { CartFragment } from '../../../graphql/magento'
import { CustomerAvailableBalance } from '../state/rewards'
import { getEligibleRebateAmount } from '../utils'

export interface RebatesState {
  maxAmount: number
  maxApplied: boolean
  rebateAmount: number
  rebateAmountInput: string
  rebateAmountPending: number
}

// `rebate` naming is kept from legacy rebate-only logic, before broader rewards were introduced
export type RebatesDispatchAction =
  | { type: 'change_rebate_input'; value: string }
  | { type: 'change_rebate_slider'; value: number }
  | {
      type: 'reset'
      availableBalance: CustomerAvailableBalance
      cart: CartFragment
      delayedBilling: boolean
    }
  | { type: 'set_rebate_amount'; value: number }

export const cartRebateInitialState = ({
  availableBalance,
  cart,
  delayedBilling,
}: {
  availableBalance: CustomerAvailableBalance
  cart: CartFragment
  delayedBilling: boolean
}): RebatesState => {
  const rebateAmount = cart?.rebate?.value ?? 0
  const maxAmount = getEligibleRebateAmount({
    cart,
    currentBalance: availableBalance?.totalBalance?.parsed ?? 0,
    delayedBilling,
  })

  return {
    maxAmount,
    maxApplied: rebateAmount >= maxAmount,
    rebateAmount,
    rebateAmountInput: rebateAmount.toFixed(2),
    rebateAmountPending: rebateAmount,
  }
}

export const cartRebateReducer = (
  state: RebatesState,
  action: RebatesDispatchAction,
): RebatesState => {
  switch (action.type) {
    case 'change_rebate_input': {
      const pending = action.value ? Number(action.value) : 0
      return {
        ...state,
        rebateAmountInput: action.value,
        rebateAmountPending: Number.isNaN(pending) ? 0 : pending,
      }
    }
    case 'change_rebate_slider': {
      return {
        ...state,
        rebateAmountInput: action.value.toFixed(2),
        rebateAmountPending: action.value,
      }
    }
    case 'reset': {
      return cartRebateInitialState(action)
    }
    case 'set_rebate_amount': {
      const rebateAmount = Math.max(Math.min(state.maxAmount, _.round(action.value, 2)), 0)
      return {
        ...state,
        maxApplied: rebateAmount >= state.maxAmount,
        rebateAmount,
        rebateAmountInput: rebateAmount.toFixed(2),
        rebateAmountPending: rebateAmount,
      }
    }
    default:
      return state
  }
}
